import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { api } from "../../lib/api";
import { ChevronMark, Wordmark } from "../ChevronMark";
import { SERVICE_CATEGORIES, RESOURCE_CATEGORIES, PROGRAM_CATEGORIES } from "../../data/categories";

const FALLBACK_SITE = {
  mission:
    "LearnYourWay provides personalized coaching and training to help learners boost confidence, unlock potential, and achieve academic and career success.",
  contact: {
    email: "hello@learnyourwayconsultants.com",
    phone: "+92 346 5469285",
    address: "Office 02, 1st Floor, Sahara Arcade, Sector A, Bahria Enclave, Islamabad, Pakistan",
  },
  socials: { linkedin: "#", instagram: "#", facebook: "#", youtube: "#" },
};

export function Footer() {
  const [site, setSite] = useState(FALLBACK_SITE);

  useEffect(() => {
    api.get("/content/site").then((r) => {
      if (r.data && r.data.mission) setSite(r.data);
    }).catch(() => {});
  }, []);

  const socials = site.socials || FALLBACK_SITE.socials;
  const contact = site.contact || FALLBACK_SITE.contact;

  const columns = [
    { title: "Services", items: SERVICE_CATEGORIES },
    { title: "Programs", items: PROGRAM_CATEGORIES },
    { title: "Resources", items: RESOURCE_CATEGORIES },
  ];

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-forest-ink text-bone">
      <div className="bg-noise pointer-events-none absolute inset-0" />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <ChevronMark light className="h-9 w-auto" />
              <Wordmark light />
            </div>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-emerald-100/60">{site.mission}</p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Linkedin, href: socials.linkedin, label: "LinkedIn" },
                { icon: Instagram, href: socials.instagram, label: "Instagram" },
                { icon: Facebook, href: socials.facebook, label: "Facebook" },
                { icon: Youtube, href: socials.youtube, label: "YouTube" },
              ].map(({ icon: Icon, href, label }) => (
                <a key={label} href={href} aria-label={label} data-testid={`social-${label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-100/15 text-emerald-100/60 transition-colors duration-200 hover:border-gold hover:text-gold">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {columns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{col.title}</p>
              <ul className="mt-5 space-y-2.5">
                {col.items.map((item) => (
                  <li key={item.slug}>
                    <Link to={item.path} data-testid={`footer-link-${item.slug}`}
                      className="text-sm text-emerald-100/60 transition-colors duration-200 hover:text-bone">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Contact</p>
            <ul className="mt-5 space-y-3.5 text-sm text-emerald-100/60">
              <li className="flex items-start gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" />
                <a href={`mailto:${contact.email}`} data-testid="footer-email" className="break-all hover:text-bone">{contact.email}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" />
                <a href={`tel:${contact.phone}`} data-testid="footer-phone" className="hover:text-bone">{contact.phone}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold/70" />
                <span data-testid="footer-address">{contact.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-emerald-100/10 pt-8 sm:flex-row">
          <p className="text-xs text-emerald-100/40">© 2026 LearnYourWay. All rights reserved.</p>
          <div className="flex gap-6 text-xs text-emerald-100/40">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="transition-colors hover:text-bone">Privacy Policy</Link>
            <Link to="/terms-of-service" data-testid="footer-terms-link" className="transition-colors hover:text-bone">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
